import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { DashboardMenu } from "../data/menu";
import { useState, useEffect } from "react";
import { Drawer } from "rsuite";
import MenuIcon from "@rsuite/icons/Menu";
import CloseIcon from "@rsuite/icons/Close";
import axios from "axios";
import { getCookies } from "cookies-next";
import { config } from "../data/config";

export default function DashboardLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState<number | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18next.language || 'fa');

  const availableLanguages = [
    { code: "fa", name: "فارسی" },
    { code: "en", name: "English" },
    { code: "ar", name: "العربية" }
  ];
  
  const changeLanguage = (lng: string) => {
    // Validate language code
    if (!['fa', 'en', 'ar'].includes(lng)) {
      console.warn('Invalid language code:', lng);
      return;
    }
    
    // Change language in i18next
    i18next.changeLanguage(lng);
    setCurrentLanguage(lng);
    
    // Save to localStorage to persist across page refreshes
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('i18nextLng', lng);
      } catch (e) {
        console.warn('Error saving to localStorage:', e);
      }
    }
  };

  // Load saved language on mount and ensure it persists
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('i18nextLng');
        if (saved && ['fa', 'en', 'ar'].includes(saved)) {
          // Ensure i18next uses the saved language
          if (i18next.language !== saved) {
            i18next.changeLanguage(saved);
          }
          setCurrentLanguage(saved);
        } else {
          // If no saved language, use current i18next language or default
          const currentLang = i18next.language || 'fa';
          localStorage.setItem('i18nextLng', currentLang);
          setCurrentLanguage(currentLang);
        }
      } catch (e) {
        console.warn('Error reading from localStorage:', e);
        // Fallback to current i18next language
        setCurrentLanguage(i18next.language || 'fa');
      }
    }
  }, []);
  
  // Listen for language changes from i18next to keep state in sync
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
      if (typeof window !== 'undefined') {
        localStorage.setItem('i18nextLng', lng);
      }
    };
    
    i18next.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18next.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close popover when clicking outside
  useEffect(() => {
    if (popoverOpen === null) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.menu-popover-container') && !target.closest('.fixed.bg-white.rounded-lg')) {
        setPopoverOpen(null);
        setPopoverPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  const sidebarWidth = sidebarCollapsed ? 80 : 256;

  return (
    <div className="App bg-gray-50 min-h-screen" dir={t("dir")}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 md:px-6 py-3 bg-white border-b border-gray-200 shadow-sm fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center gap-3">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>

          {/* Desktop Sidebar Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title={sidebarCollapsed ? t("Open Menu") : t("Close Menu")}
          >
            <svg
              className={`w-5 h-5 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {t("dir") === "rtl" ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              )}
            </svg>
          </button>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          <select
            onChange={(e) => changeLanguage(e.target.value)}
            value={currentLanguage}
            className="bg-gray-50 text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
          >
            {availableLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
          <a
            href="/logout"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors font-medium text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="hidden sm:block">{t("Logout")}</span>
          </a>
        </div>
      </header>

      {/* Main layout wrapper */}
      <div className="flex flex-col md:flex-row min-h-screen pt-16">
        {/* Sidebar (desktop) */}
        <aside
          className={`hidden md:flex flex-col bg-white border-r border-gray-200 text-gray-700 fixed top-16 bottom-0 transition-all duration-300 shadow-sm overflow-visible
            ${t("dir") === "rtl" ? "right-0" : "left-0"}`}
          style={{ width: `${sidebarWidth}px`, zIndex: 30 }}
        >
          {/* Sidebar Header */}
          <div className="h-16 flex items-center justify-center border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            {!sidebarCollapsed && (
              <span className="font-bold text-lg text-gray-800">{t("Dashboard")}</span>
            )}
            {sidebarCollapsed && (
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto overflow-x-visible py-2" style={{ overflowX: 'visible' }}>
            {DashboardMenu.map((item: any, index: number) => (
              <div key={index} className="mb-1">
                {item.sub.length === 0 ? (
                  <a
                    href={item.url}
                    className={`
                      flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all
                      ${location.pathname === item.url 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                    title={sidebarCollapsed ? t(item.name) : ''}
                  >
                    {item.icon && (
                      <img src={item.icon} alt="" className="w-5 h-5 flex-shrink-0" />
                    )}
                    {!sidebarCollapsed && (
                      <span className="font-medium text-sm">{t(item.name)}</span>
                    )}
                  </a>
                ) : (
                  <div className="relative menu-popover-container">
                    {sidebarCollapsed ? (
                      <>
                        <button
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            if (popoverOpen === index) {
                              setPopoverOpen(null);
                              setPopoverPosition(null);
                            } else {
                              setPopoverOpen(index);
                              setPopoverPosition({
                                top: rect.top,
                                ...(t("dir") === "rtl" 
                                  ? { right: window.innerWidth - rect.left }
                                  : { left: rect.right + 8 }
                                )
                              });
                            }
                          }}
                          className={`
                            menu-item-button flex items-center justify-center px-4 py-3 mx-2 rounded-lg cursor-pointer transition-all w-full
                            ${popoverOpen === index 
                              ? 'bg-blue-50 text-blue-700' 
                              : 'text-gray-700 hover:bg-gray-100'
                            }
                          `}
                          title={t(item.name)}
                        >
                          {item.icon && (
                            <img src={item.icon} alt="" className="w-5 h-5 flex-shrink-0" />
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          onClick={() => setOpenMenu(openMenu === index ? null : index)}
                          className={`
                            flex items-center justify-between px-4 py-3 mx-2 rounded-lg cursor-pointer transition-all
                            ${openMenu === index 
                              ? 'bg-blue-50 text-blue-700' 
                              : 'text-gray-700 hover:bg-gray-100'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            {item.icon && (
                              <img src={item.icon} alt="" className="w-5 h-5 flex-shrink-0" />
                            )}
                            <span className="font-medium text-sm">{t(item.name)}</span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform ${openMenu === index ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {openMenu === index && (
                          <div className="bg-gray-50 border-r-2 border-blue-600">
                            {item.sub.map((sub: any, idx: number) => (
                              <a
                                key={idx}
                                href={sub.url}
                                className={`
                                  flex items-center gap-3 px-6 py-2.5 text-sm transition-all
                                  ${location.pathname === sub.url
                                    ? 'bg-blue-600 text-white font-medium'
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                                  }
                                `}
                              >
                                {sub.icon && (
                                  <img src={sub.icon} alt="" className="w-4 h-4 flex-shrink-0" />
                                )}
                                {t(sub.name)}
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          {!sidebarCollapsed && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="text-xs text-gray-500 text-center">
                © {new Date().getFullYear()}
              </div>
            </div>
          )}
        </aside>

        {/* Popover for collapsed sidebar submenus */}
        {sidebarCollapsed && popoverOpen !== null && popoverPosition && (
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 min-w-[220px] overflow-hidden z-[9999]"
            style={{
              top: `${popoverPosition.top}px`,
              ...(popoverPosition.left !== undefined ? { left: `${popoverPosition.left}px` } : {}),
              ...(popoverPosition.right !== undefined ? { right: `${popoverPosition.right}px` } : {}),
            }}
          >
            <div className="px-4 py-2 font-semibold text-sm text-gray-700 border-b border-gray-200 bg-gray-50">
              {t(DashboardMenu[popoverOpen]?.name)}
            </div>
            <div className="py-1">
              {DashboardMenu[popoverOpen]?.sub?.map((sub: any, idx: number) => (
                <a
                  key={idx}
                  href={sub.url}
                  onClick={() => {
                    setPopoverOpen(null);
                    setPopoverPosition(null);
                  }}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 text-sm transition-all
                    ${location.pathname === sub.url
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
                    }
                  `}
                >
                  {sub.icon && (
                    <img src={sub.icon} alt="" className="w-4 h-4 flex-shrink-0" />
                  )}
                  {t(sub.name)}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Sidebar (mobile drawer) */}
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          placement={t("dir") === "rtl" ? "right" : "left"}
          size="xs"
        >
          <Drawer.Header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <Drawer.Title className="text-white font-bold">{t("Dashboard")}</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body className="p-0">
            <nav>
              {DashboardMenu.map((item: any, index: number) => (
                <div key={index}>
                  {item.sub.length === 0 ? (
                    <a
                      href={item.url}
                      className={`
                        flex items-center gap-3 px-4 py-3 border-b border-gray-100 transition-colors
                        ${location.pathname === item.url
                          ? 'bg-blue-50 text-blue-700 font-medium border-r-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                        }
                      `}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.icon && (
                        <img src={item.icon} alt="" className="w-5 h-5" />
                      )}
                      {t(item.name)}
                    </a>
                  ) : (
                    <div>
                      <div
                        onClick={() => setOpenMenu(openMenu === index ? null : index)}
                        className={`
                          flex items-center justify-between px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors
                          ${openMenu === index ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          {item.icon && (
                            <img src={item.icon} alt="" className="w-5 h-5" />
                          )}
                          <span className="font-medium">{t(item.name)}</span>
                        </div>
                        <svg
                          className={`w-4 h-4 transition-transform ${openMenu === index ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {openMenu === index && (
                        <div className="bg-gray-50">
                          {item.sub.map((sub: any, idx: number) => (
                            <a
                              key={idx}
                              href={sub.url}
                              className={`
                                flex items-center gap-3 px-8 py-2.5 text-sm transition-colors
                                ${location.pathname === sub.url
                                  ? 'bg-blue-600 text-white font-medium'
                                  : 'text-gray-700 hover:bg-gray-100'
                                }
                              `}
                              onClick={() => setMobileOpen(false)}
                            >
                              {sub.icon && (
                                <img src={sub.icon} alt="" className="w-4 h-4" />
                              )}
                              {t(sub.name)}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </Drawer.Body>
        </Drawer>

        {/* Main Content */}
        <main
          className={`flex-1 transition-all duration-300 ${
            t("dir") === "rtl" ? "md:mr-0" : "md:ml-0"
          }`}
          style={{
            marginRight: t("dir") === "rtl" ? (sidebarCollapsed ? "80px" : "256px") : "0",
            marginLeft: t("dir") === "rtl" ? "0" : (sidebarCollapsed ? "80px" : "256px"),
          }}
        >
          <div className="p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
